import clsx from "clsx";
import { usePage, useSection } from "@tree-company/tree-query";
import { useDebounce } from "../helpers/helpers";
import { Progress } from "./progress";
import { totalQuestions } from "../survey";
import { useEffect, useState } from "react";
import { RadioButton } from "./radio_button";
import locales from "../locales";
import t from "../locales";

export const QuestionPage = ({
  className,
  page,
  submitting,
  handleSave,
  onPreviousPage,
}) => {
  const [transitionClass, setTransitionClass] = useState(
    page.order === 1 ? "" : "page--enter",
  );
  const [submitted, setSubmitted] = useState(false);
  const debug = window.location.search.includes("debug");
  const { htmlAttributes, disablePrevious, order, sections } = usePage(page);
  const debouncedSubmit = useDebounce(handleNext, debug ? 0 : 250);

  function handleNext(e) {
    if (submitted) return;
    setTransitionClass("page--leave");
    setSubmitted(true);
    setTimeout(() => handleSave(e), 375);
  }

  useEffect(() => {
    setTimeout(() => setTransitionClass(""), 375);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (page.treesponse.getAnswer("groupCode")) return;
    page.treesponse.setAnswer("groupCode", params.get("classCode"));
  }, []);

  return (
    <>
      <title>{`${order}/${totalQuestions} - ${locales("intro.title")}`}</title>
      {/* This assumes we only have one question on this page! */}
      <form
        className={clsx(
          "page",
          className,
          htmlAttributes.class,
          transitionClass,
        )}
        onChange={debouncedSubmit}
      >
        <div className="question-container">
          <Progress
            disableNext={sections[0].value === null}
            disablePrevious={submitting || disablePrevious}
            onPreviousPage={onPreviousPage}
            onNextPage={handleNext}
          >
            <h1 className="page__progress-text">
              {order}/{totalQuestions}
            </h1>
          </Progress>
          <ButtonQuestion
            question={sections[0]}
            className={"page__section"}
            onChange={debouncedSubmit}
          ></ButtonQuestion>
          <div className="question__indicator --hide-lg">
            <span className="question__indicator-text">
              {t("questions.buttons.helemaal_oneens")}
            </span>
            <span className="question__indicator-text">
              {t("questions.buttons.helemaal_eens")}
            </span>
          </div>
        </div>
      </form>
    </>
  );
};

export function ButtonQuestion({ className, question, onChange }) {
  const {
    value,
    title,
    key,
    required,
    hint,
    errorMessages,
    valid,
    skip,
    options,
    disabled,
    ariaDisabled,
    htmlAttributes,
  } = useSection(question);

  function handleChange(event, value) {
    if (ariaDisabled) return;

    question.value = value;
    question.validate();
    if (onChange) onChange(event);
  }

  if (skip) return null;

  return (
    <>
      <fieldset
        aria-labelledby="questionLabel"
        className={clsx(
          "question",
          "question--radio",
          "question--radio-buttons",
          htmlAttributes.class,
          { "question--required": required },
          { "question--invalid": !valid },
          { "question--disabled": disabled || ariaDisabled },
          className,
        )}
        name={key}
        disabled={disabled}
        aria-disabled={ariaDisabled}
      >
        <h2 id="questionLabel" className="question__label">
          {title}
        </h2>
        <div className="question__options">
          {options.map((o) => (
            <div
              className={clsx(
                "question__option",
                "option option--button",
                o.htmlAttributes.class,
                {
                  "option--disabled": o.disabled || o.ariaDisabled,
                },
              )}
              key={o.value.toString()}
            >
              <button
                id={[key, o.value].join("-")}
                aria-label={o.label}
                className={clsx("button option__label")}
                data-checked={value === o.value}
                data-value={o.value}
                onClick={(e) => {
                  e.preventDefault();

                  if (o.ariaDisabled) return;
                  handleChange(e, o.value);
                }}
                disabled={o.disabled}
                aria-disabled={o.ariaDisabled}
              >
                <RadioButton value={o.value} checked={value === o.value} />
                <p className="option__text --show-lg">{o.label}</p>
              </button>
            </div>
          ))}
        </div>
        {hint && <span className="question__hint">{hint}</span>}
        {errorMessages.length > 0 && (
          <span className="question__error">{errorMessages[0]}</span>
        )}
      </fieldset>
    </>
  );
}
